<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import EmailComposePreview from './EmailComposePreview.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import WhatsappInteractiveModal from './WhatsappInteractive/Modal.vue';
import PixPaymentModal from './WhatsappInteractive/PixPaymentModal.vue';
import WhatsappFlowSendModal from './WhatsappFlow/SendModal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import {
  stripDraftSignature,
  withInlineSignature,
} from 'dashboard/helper/composerSignature';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  removeSignature,
  getEffectiveChannelType,
  getAgentVariables,
  getContactVariables,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useMacroExecution } from 'dashboard/composables/useMacroExecution';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    NextSelect,
    EmojiIconPicker,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    EmailComposePreview,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WhatsappInteractiveModal,
    PixPaymentModal,
    WhatsappFlowSendModal,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    ConversationResolveAttributesModal,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const whatsappInteractiveModal = useTemplateRef('whatsappInteractiveModal');
    const pixPaymentModal = useTemplateRef('pixPaymentModal');
    const whatsappFlowModal = useTemplateRef('whatsappFlowModal');
    const copilot = useCopilotReply();
    const macroExecution = useMacroExecution();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    return {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      whatsappInteractiveModal,
      pixPaymentModal,
      whatsappFlowModal,
      copilot,
      shortcutKey,
      macroExecution,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      draftConversationId: null,
      draftReplyMode: null,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      shortcutWhatsAppTemplate: null,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      showMacrosMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      privateNoteExpiryHours: 0,
      isSendingInteractive: false,
      isSendingPix: false,
      isSendingWhatsappFlow: false,
      temporaryPrivateCopy: {
        title: 'Nota privada temporária',
        description: 'Após o prazo, o texto e os anexos serão apagados.',
        permanentTitle: 'Nota privada',
        permanentDescription: 'A nota ficará salva na conversa.',
        visibility: 'Visível somente para a equipe',
        expiryLabel: 'Expiração',
      },
    };
  },
  computed: {
    isMobileComposerCollapsed: {
      get() {
        return this.uiSettings.is_reply_composer_collapsed === true;
      },
      set(value) {
        this.updateUISettings({ is_reply_composer_collapsed: value });
      },
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      isMetaMessageSendingDisabled: 'globalConfig/isMetaMessageSendingDisabled',
      getAccount: 'accounts/getAccount',
    }),
    currentAccountName() {
      return this.getAccount(this.currentChat?.account_id)?.name || '';
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    // Envio direto de WhatsApp Flow: somente respostas públicas em caixas WhatsApp Cloud.
    showWhatsappFlowAction() {
      return this.isAWhatsAppCloudChannel && !this.isPrivate;
    },
    isWithinMessagingWindow() {
      return !!(
        this.currentChat?.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      );
    },
    canSendPublicReply() {
      return (
        this.isWithinMessagingWindow &&
        !this.isBotOwnedPendingConversation &&
        !this.isInstagramReplyRestricted
      );
    },
    isInstagramReplyRestricted() {
      return this.isMetaMessageSendingDisabled && this.isAnInstagramChannel;
    },
    isReplyRestricted() {
      return (
        this.isBotOwnedPendingConversation ||
        this.isInstagramReplyRestricted ||
        (!this.currentChat?.can_reply &&
          !(this.isAWhatsAppChannel || this.isAPIInbox))
      );
    },
    isPrivate() {
      return (
        !this.canSendPublicReply || this.replyType === REPLY_EDITOR_MODES.NOTE
      );
    },
    isOnPrivateNote() {
      if (this.isInstagramReplyRestricted) {
        return true;
      }

      return this.isBotOwnedPendingConversation
        ? this.isPrivate
        : this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    effectiveReplyMode() {
      return this.isOnPrivateNote
        ? REPLY_EDITOR_MODES.NOTE
        : REPLY_EDITOR_MODES.REPLY;
    },
    privateNoteDefaultExpiryHours() {
      const configuredValue = this.getAccount(this.currentChat?.account_id)
        ?.settings?.temporary_private_message_default_expiry_hours;
      const configuredHours =
        configuredValue === undefined ||
        configuredValue === null ||
        configuredValue === ''
          ? 0
          : Number(configuredValue);
      return Math.min(720, Math.max(0, configuredHours || 0));
    },
    privateNoteExpiryOptions() {
      const hours = [
        0,
        1,
        6,
        12,
        24,
        72,
        168,
        720,
        this.privateNoteDefaultExpiryHours,
      ];
      return [...new Set(hours)]
        .sort((first, second) => first - second)
        .map(value => {
          let label = `${value / 24} dia${value === 24 ? '' : 's'}`;
          if (value === 0) label = 'Nunca expira';
          if (value > 0 && (value < 24 || value % 24 !== 0)) {
            label = `${value} hora${value === 1 ? '' : 's'}`;
          }
          return { value, label };
        });
    },
    privateNoteIsTemporary() {
      return Number(this.privateNoteExpiryHours) > 0;
    },
    hasMeaningfulEditorContent() {
      const body = this.message || '';
      if (!this.isAnEmailChannel) return !!body.trim();
      // Only strip the signature when it's actually being auto-appended.
      // If the toggle is off, the agent's text might happen to match their
      // saved signature and we'd incorrectly treat it as empty.
      const shouldStripSignature =
        !this.isPrivate && this.sendWithSignature && !!this.messageSignature;
      if (!shouldStripSignature) return !!body.trim();
      const stripped = removeSignature(
        body,
        this.messageSignature,
        getEffectiveChannelType(this.channelType, this.inbox?.medium || '')
      );
      return !!stripped.trim();
    },
    isBotOwnedPendingConversation() {
      return (
        this.currentChat?.status === wootConstants.STATUS_TYPE.PENDING &&
        this.currentChat?.meta?.assignee_type === 'AgentBot'
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.isPrivate) {
        return this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT');
      }
      return this.isAnEmailChannel
        ? this.$t('CONVERSATION.FOOTER.EMAIL_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    mobileComposerCollapsedLabel() {
      return this.$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED');
    },
    collapsedComposerTitle() {
      return this.isPrivate
        ? this.$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_PRIVATE')
        : this.$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_REPLY');
    },
    // Prévia do rascunho em texto simples, sem a marcação do editor.
    collapsedDraftPreview() {
      return String(this.message || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/[*_`~#>[\]]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'is-mobile-collapsed': this.isMobileComposerCollapsed,
      };
    },
    isCompactComposer() {
      return (
        (this.isPrivate || !this.isAnEmailChannel) &&
        !this.isOnInteractiveMessage &&
        !this.isEditorDisabled &&
        !this.showAudioRecorderEditor &&
        !this.copilot.isActive.value &&
        !this.hasAttachments &&
        !this.shouldShowReplyToMessage
      );
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnInteractiveMessage() {
      return this.replyType === REPLY_EDITOR_MODES.INTERACTIVE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return this.isAnEmailChannel
        ? !!this.emailSignature
        : !!this.messageSignature;
    },
    // E-mail usa a assinatura própria de e-mail (Perfil); sem ela, a assinatura de mensagens.
    emailSignature() {
      return (
        this.uiSettings?.email_signature?.trim() || this.messageSignature || ''
      );
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.effectiveReplyMode}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppCloudChannel) {
        return AUDIO_FORMATS.OGG;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      // Match the backend drops: names are Ruby-capitalized and
      // {{agent.*}} is the message sender, not the assignee.
      return {
        ...variables,
        ...getContactVariables(this.currentContact),
        ...getAgentVariables(this.currentUser),
      };
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    privateNoteDefaultExpiryHours: {
      immediate: true,
      handler(hours) {
        this.privateNoteExpiryHours = hours;
      },
    },
    currentChat(conversation, oldConversation) {
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isInstagramReplyRestricted) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
        return;
      }

      if (this.isOnPrivateNote) {
        return;
      }

      this.replyType = this.isWithinMessagingWindow
        ? REPLY_EDITOR_MODES.REPLY
        : REPLY_EDITOR_MODES.NOTE;

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.switchDraftContext(conversationId, this.effectiveReplyMode);
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    showWhatsappTemplates(isAvailable) {
      if (!isAvailable) this.hideWhatsappTemplatesModal();
    },
    showContentTemplates(isAvailable) {
      if (!isAvailable) this.hideContentTemplatesModal();
    },
    effectiveReplyMode(updatedReplyType) {
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode: updatedReplyType,
      });
      this.switchDraftContext(this.conversationIdByRoute, updatedReplyType);
    },
  },

  mounted() {
    if (this.isInstagramReplyRestricted) {
      this.replyType = REPLY_EDITOR_MODES.NOTE;
    }

    this.$store.dispatch('draftMessages/setReplyEditorMode', {
      mode: this.effectiveReplyMode,
    });
    this.switchDraftContext(
      this.conversationIdByRoute,
      this.effectiveReplyMode
    );
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.effectiveReplyMode);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  methods: {
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.effectiveReplyMode
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.effectiveReplyMode) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    switchDraftContext(conversationId, replyMode) {
      if (
        this.draftConversationId === conversationId &&
        this.draftReplyMode === replyMode
      ) {
        return;
      }

      if (this.draftConversationId) {
        this.setToDraft(this.draftConversationId, this.draftReplyMode);
      }

      this.draftConversationId = conversationId;
      this.draftReplyMode = replyMode;
      this.getFromDraft();
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // A assinatura fica fora do campo também no e-mail (aplicada no envio); rascunhos
      // antigos com a assinatura inserida são limpos.
      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );
      const withoutMessageSignature = stripDraftSignature(
        message,
        this.messageSignature,
        effectiveChannelType
      );
      return this.isAnEmailChannel
        ? stripDraftSignature(
            withoutMessageSignature,
            this.emailSignature,
            effectiveChannelType
          )
        : withoutMessageSignature;
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        !this.showMacrosMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    toggleMacrosMenu(value) {
      this.showMacrosMenu = value;
    },
    onExecuteMacro(macro) {
      const pending = this.macroExecution.execute(macro, this.currentChat.id);
      if (pending) {
        this.$refs.resolveAttributesModal?.open(
          pending.missing,
          pending.customAttributes
        );
      }
    },
    async attachCannedResponseFiles(files = []) {
      await Promise.all(
        files.map(async storedFile => {
          try {
            if (storedFile.signed_id) {
              this.attachedFiles.push({
                currentChatId: this.currentChat.id,
                resource: {
                  filename: storedFile.filename,
                  content_type: storedFile.file_type,
                  byte_size: storedFile.byte_size || 0,
                },
                isPrivate: this.isPrivate,
                thumb: storedFile.file_url,
                blobSignedId: storedFile.signed_id,
                isVoiceMessage: false,
              });
              return;
            }

            const response = await fetch(storedFile.file_url, {
              credentials: 'same-origin',
            });
            if (!response.ok) throw new Error('Unable to load attachment');

            const blobData = await response.blob();
            const browserFile = new File([blobData], storedFile.filename, {
              type: storedFile.file_type || blobData.type,
            });
            const file = {
              name: browserFile.name,
              type: browserFile.type,
              size: browserFile.size,
              file: browserFile,
            };
            const blob = this.globalConfig.directUploadsEnabled
              ? { signed_id: storedFile.signed_id }
              : null;

            this.attachFile({ blob, file });
          } catch {
            useAlert(this.$t('CANNED_MGMT.EXECUTION.ATTACHMENT_ERROR'));
          }
        })
      );
    },
    async onExecuteCannedResponse(response) {
      const type = response.response_type || 'text';

      if (type === 'attachment') {
        this.addIntoEditor(response.content);
        await this.attachCannedResponseFiles(response.files);
        return;
      }

      if (type === 'whatsapp_template') {
        if (!this.isAWhatsAppChannel) {
          useAlert(this.$t('CANNED_MGMT.EXECUTION.WHATSAPP_ONLY'));
          return;
        }
        this.openWhatsappTemplateModal(
          response.content_attributes?.whatsapp_template
        );
        return;
      }

      if (type === 'whatsapp_interactive') {
        if (!this.isAWhatsAppCloudChannel || this.isReplyRestricted) {
          useAlert(this.$t('CANNED_MGMT.EXECUTION.INTERACTIVE_UNAVAILABLE'));
          return;
        }
        this.replyType = REPLY_EDITOR_MODES.INTERACTIVE;
        this.$nextTick(() => {
          this.whatsappInteractiveModal?.open(
            response.content_attributes?.interactive_template
          );
        });
      }
    },
    openWhatsappTemplateModal(template = null) {
      this.shortcutWhatsAppTemplate = template;
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
      this.shortcutWhatsAppTemplate = null;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
        return true;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
        return false;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      this.$store.dispatch('draftMessages/setReplyEditorMode', { mode });
      if (this.canSendPublicReply) this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
      if (mode === REPLY_EDITOR_MODES.INTERACTIVE) {
        if (!this.isAWhatsAppCloudChannel || this.isReplyRestricted) {
          this.replyType = REPLY_EDITOR_MODES.REPLY;
          return;
        }
        this.$nextTick(() => this.whatsappInteractiveModal?.open());
      }
    },
    openWhatsappFlowModal() {
      if (!this.showWhatsappFlowAction) return;
      this.whatsappFlowModal?.open();
    },
    async onSendWhatsappFlow(messagePayload) {
      if (this.isSendingWhatsappFlow) return;
      this.isSendingWhatsappFlow = true;
      try {
        await this.$store.dispatch('createPendingMessageAndSend', {
          conversationId: this.currentChat.id,
          sender: this.sender,
          ...messagePayload,
        });
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.whatsappFlowModal?.close();
      } catch (error) {
        this.whatsappFlowModal?.showError(error?.response?.data?.error);
      } finally {
        this.isSendingWhatsappFlow = false;
      }
    },
    closeInteractiveMessageModal() {
      this.replyType = REPLY_EDITOR_MODES.REPLY;
    },
    async onSendInteractiveMessage(messagePayload) {
      this.isSendingInteractive = true;
      const sent = await this.sendMessage({
        conversationId: this.currentChat.id,
        sender: this.sender,
        ...messagePayload,
      });
      this.isSendingInteractive = false;
      if (!sent) return;

      this.whatsappInteractiveModal?.close();
      this.replyType = REPLY_EDITOR_MODES.REPLY;
    },
    openPixPaymentModal() {
      if (
        !this.isAWhatsAppCloudChannel ||
        this.isReplyRestricted ||
        this.isPrivate
      )
        return;
      this.pixPaymentModal?.open();
    },
    async onSendPixPayment(messagePayload) {
      if (this.isSendingPix || this.isReplyRestricted || this.isPrivate) return;
      this.isSendingPix = true;
      try {
        const sent = await this.sendMessage({
          conversationId: this.currentChat.id,
          sender: this.sender,
          ...messagePayload,
        });
        if (sent) this.pixPaymentModal?.close();
      } finally {
        this.isSendingPix = false;
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.effectiveReplyMode);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isVoiceMessage to the file to identify recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isVoiceMessage: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    onRecordError() {
      this.toggleAudioRecorder();
      useAlert(this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isVoiceMessage: file?.isVoiceMessage || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    withOutgoingSignature(message) {
      if (this.isPrivate || !this.sendWithSignature) return message;
      if (this.isAnEmailChannel) {
        return message.trim() && this.emailSignature
          ? `${message.trimEnd()}\n\n${this.emailSignature}`
          : message;
      }
      return !this.isPrivate && !this.isAnEmailChannel && this.sendWithSignature
        ? withInlineSignature(message, this.messageSignature, {
            layout: this.uiSettings?.message_signature_layout,
            channelType: getEffectiveChannelType(
              this.channelType,
              this.inbox?.medium || ''
            ),
          })
        : message;
    },
    openEmailPreview() {
      this.$refs.emailComposePreview?.open(
        this.withOutgoingSignature(this.message || '')
      );
    },
    getMultipleMessagesPayload(message) {
      message = this.withOutgoingSignature(message);
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile =
            attachment.blobSignedId || attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
            isVoiceMessage: attachment.isVoiceMessage || false,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(
        this.withOutgoingSignature(message)
      );

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      if (this.isPrivate && this.privateNoteIsTemporary) {
        const expiresInHours = Number(this.privateNoteExpiryHours);
        messagePayload.contentAttributes = {
          temporary_private: true,
          expires_in_hours: expiresInHours,
          expires_at: new Date(
            Date.now() + expiresInHours * 60 * 60 * 1000
          ).toISOString(),
        };
      }
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (attachment.blobSignedId) {
            messagePayload.files.push(attachment.blobSignedId);
            if (attachment.isVoiceMessage) {
              messagePayload.isVoiceMessage = true;
            }
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      // Sem destinatário no último e-mail, o envio vai ao contato: o campo "Para" já mostra isso.
      this.toEmails = to.filter(Boolean).join(', ') || conversationContact;
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isVoiceMessage
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField?.());
    },
    toggleMobileComposer() {
      this.isMobileComposerCollapsed = !this.isMobileComposerCollapsed;
      if (!this.isMobileComposerCollapsed) {
        this.$nextTick(() => this.messageEditor?.focusEditorInputField?.());
      }
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
    <button
      v-if="isMobileComposerCollapsed"
      type="button"
      class="reply-box__mobile-collapsed-bar group"
      :aria-label="mobileComposerCollapsedLabel"
      :aria-expanded="false"
      @click="toggleMobileComposer"
    >
      <span class="reply-box__collapsed-icon" aria-hidden="true">
        <span
          class="size-4"
          :class="
            isPrivate ? 'i-hugeicons-square-lock-02' : 'i-hugeicons-message-01'
          "
        />
      </span>
      <span class="flex min-w-0 flex-1 flex-col items-start text-start">
        <span class="text-sm font-medium leading-5 text-n-slate-12">
          {{ collapsedComposerTitle }}
        </span>
        <span class="w-full truncate text-xs leading-4 text-n-slate-11">
          <template v-if="collapsedDraftPreview">
            <span class="font-medium text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_DRAFT') }}
            </span>
            {{ collapsedDraftPreview }}
          </template>
          <template v-else>
            {{ $t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_HINT') }}
          </template>
        </span>
      </span>
      <span
        v-if="attachedFiles.length"
        class="reply-box__collapsed-chip"
        :title="$t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_ATTACHMENTS')"
      >
        <span class="i-hugeicons-attachment-01 size-3.5" aria-hidden="true" />
        {{ attachedFiles.length }}
      </span>
      <span class="reply-box__collapsed-open" aria-hidden="true">
        <span class="i-lucide-chevron-up size-4" />
        <span class="hidden sm:inline">
          {{ $t('CONVERSATION.REPLYBOX.COMPOSER_COLLAPSED_OPEN') }}
        </span>
      </span>
    </button>
    <div class="reply-box__content">
      <ReplyTopPanel
        :channel-type="channelType"
        :mode="effectiveReplyMode"
        :conversation-id="conversationId"
        :is-reply-restricted="isReplyRestricted"
        :disabled="
          (copilot.isActive.value && copilot.isButtonDisabled.value) ||
          showAudioRecorderEditor
        "
        :is-editor-disabled="isEditorDisabled"
        :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
        :characters-remaining="charactersRemaining"
        :editor-content="message"
        :has-content="hasMeaningfulEditorContent"
        :enable-interactive="isAWhatsAppCloudChannel"
        @set-reply-mode="setReplyMode"
        @open-pix="openPixPaymentModal"
        @toggle-editor-size="toggleEditorSize"
        @collapse-composer="toggleMobileComposer"
        @toggle-copilot="copilot.toggleEditor"
        @execute-copilot-action="executeCopilotAction"
      />
      <ArticleSearchPopover
        v-if="showArticleSearchPopover && connectedPortalSlug"
        :selected-portal-slug="connectedPortalSlug"
        @insert="handleInsert"
        @close="onSearchPopoverClose"
      />
      <div
        v-if="isPrivate && isDefaultEditorMode"
        class="reply-box__note-settings"
        :class="{ 'is-joined': isCompactComposer }"
      >
        <div
          class="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2"
        >
          <div class="flex min-w-0 items-center gap-2">
            <span
              class="i-lucide-lock-keyhole size-4 shrink-0 text-n-amber-11"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <p class="mb-0 text-xs font-semibold text-n-amber-12">
                {{ temporaryPrivateCopy.permanentTitle }}
              </p>
              <p class="mb-0 mt-0.5 text-xs text-n-slate-11">
                {{ temporaryPrivateCopy.visibility }}
              </p>
            </div>
          </div>
          <label class="reply-box__note-expiry">
            <span
              class="i-lucide-timer size-4 shrink-0 text-n-amber-11"
              aria-hidden="true"
            />
            <span class="text-xs font-medium text-n-slate-11">
              {{ temporaryPrivateCopy.expiryLabel }}
            </span>
            <NextSelect
              v-model="privateNoteExpiryHours"
              :options="privateNoteExpiryOptions"
              :aria-label="temporaryPrivateCopy.expiryLabel"
              class="[&>select]:h-8 [&>select]:min-w-28 [&>select]:bg-n-solid-amber [&>select]:py-1 [&>select]:text-xs"
            />
          </label>
        </div>
        <p class="reply-box__note-expiry-help" aria-live="polite">
          {{
            privateNoteIsTemporary
              ? temporaryPrivateCopy.description
              : temporaryPrivateCopy.permanentDescription
          }}
        </p>
      </div>
      <div
        class="reply-box__composer-shell"
        :class="{
          'is-compact': isCompactComposer,
          'is-note': isPrivate && isDefaultEditorMode,
          'is-restricted': isEditorDisabled && !isOnInteractiveMessage,
        }"
      >
        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
        >
          <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
            <ReplyToMessage
              v-if="shouldShowReplyToMessage"
              :message="inReplyTo"
              @dismiss="resetReplyToMessage"
            />
            <ReplyEmailHead
              v-if="showReplyHead && isDefaultEditorMode"
              v-model:cc-emails="ccEmails"
              v-model:bcc-emails="bccEmails"
              v-model:to-emails="toEmails"
              @preview="openEmailPreview"
            />
            <EmailComposePreview
              ref="emailComposePreview"
              :to-emails="toEmails"
              :cc-emails="ccEmails"
              :subject="currentChat?.additional_attributes?.mail_subject || ''"
            />
            <AudioRecorder
              v-if="showAudioRecorderEditor"
              ref="audioRecorderInput"
              :audio-record-format="audioRecordFormat"
              :is-send-disabled="isReplyButtonDisabled"
              @discard="resetAudioRecorderInput"
              @send="onSendReply"
              @recorder-progress-changed="onRecordProgressChanged"
              @finish-record="onFinishRecorder"
              @record-error="onRecordError"
              @play="recordingAudioState = 'playing'"
              @pause="recordingAudioState = 'paused'"
            />
            <CopilotEditorSection
              v-if="copilot.isActive.value && !showAudioRecorderEditor"
              :show-copilot-editor="copilot.showEditor.value"
              :is-generating-content="copilot.isGenerating.value"
              :generated-content="copilot.generatedContent.value"
              :usage="copilot.usage.value"
              :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
              @focus="onFocus"
              @blur="onBlur"
              @clear-selection="clearEditorSelection"
              @close="copilot.showEditor.value = false"
              @content-ready="copilot.setContentReady"
              @send="copilot.sendFollowUp"
            />
            <WootMessageEditor
              v-else-if="
                !showAudioRecorderEditor &&
                !isOnInteractiveMessage &&
                !isEditorDisabled
              "
              ref="messageEditor"
              v-model="message"
              :conversation-id="conversationId"
              :editor-id="editorStateId"
              class="input popover-prosemirror-menu"
              :is-private="isOnPrivateNote"
              :placeholder="messagePlaceHolder"
              :update-selection-with="updateEditorSelectionWith"
              :min-height="isCompactComposer ? 1.5 : 4"
              :disabled="isEditorDisabled"
              enable-variables
              :variables="messageVariables"
              :signature="messageSignature"
              :allow-signature="false"
              :channel-type="channelType"
              :medium="inbox.medium"
              @typing-off="onTypingOff"
              @typing-on="onTypingOn"
              @focus="onFocus"
              @blur="onBlur"
              @toggle-user-mention="toggleUserMention"
              @toggle-canned-menu="toggleCannedMenu"
              @toggle-variables-menu="toggleVariablesMenu"
              @clear-selection="clearEditorSelection"
              @execute-copilot-action="executeCopilotAction"
              @execute-canned-response="onExecuteCannedResponse"
            />

            <div
              v-else-if="isEditorDisabled && !isOnInteractiveMessage"
              class="reply-box__restricted"
            >
              <span class="reply-box__restricted-icon">
                <span class="i-lucide-clock-alert size-5" />
              </span>
              <div class="min-w-0">
                <p class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED') }}
                </p>
                <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                  {{ messagePlaceHolder }}
                </p>
              </div>
            </div>

            <QuotedEmailPreview
              v-if="shouldShowQuotedPreview && isDefaultEditorMode"
              :quoted-email-text="quotedEmailText"
              :preview-text="quotedEmailPreviewText"
              class="mb-2"
              @toggle="toggleQuotedReply"
            />

            <div
              v-if="hasAttachments && isDefaultEditorMode"
              class="bg-transparent py-0 mb-2"
              @paste="onPaste"
            >
              <AttachmentPreview
                class="mt-2"
                :attachments="attachedFiles"
                @remove-attachment="removeAttachment"
              />
            </div>
            <MessageSignatureMissingAlert
              v-if="
                isSignatureEnabledForInbox &&
                !isSignatureAvailable &&
                isDefaultEditorMode
              "
              class="mb-2"
            />
          </div>
        </Transition>

        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
        >
          <CopilotReplyBottomPanel
            v-if="copilot.isActive.value"
            key="copilot-bottom-panel"
            :is-generating-content="copilot.isButtonDisabled.value"
            @submit="onSubmitCopilotReply"
            @cancel="copilot.reset"
          />
          <ReplyBottomPanel
            v-else-if="!isOnInteractiveMessage && !showAudioRecorderEditor"
            key="reply-bottom-panel"
            :compact="isCompactComposer"
            :conversation-id="conversationId"
            :enable-multiple-file-upload="enableMultipleFileUpload"
            :enable-whats-app-templates="showWhatsappTemplates"
            :enable-content-templates="showContentTemplates"
            :enable-whatsapp-flow="showWhatsappFlowAction"
            :inbox="inbox"
            :is-on-private-note="isOnPrivateNote"
            :is-recording-audio="isRecordingAudio"
            :is-send-disabled="isReplyButtonDisabled"
            :is-note="isPrivate"
            :is-editor-disabled="isEditorDisabled"
            :on-file-upload="onFileUpload"
            :on-send="onSendReply"
            :conversation-type="conversationType"
            :recording-audio-duration-text="recordingAudioDurationText"
            :recording-audio-state="recordingAudioState"
            :send-button-text="replyButtonLabel"
            :show-audio-recorder="showAudioRecorder"
            :show-emoji-picker="showEmojiPicker"
            :show-file-upload="showFileUpload"
            :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
            :quoted-reply-enabled="quotedReplyPreference"
            :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
            :toggle-audio-recorder="toggleAudioRecorder"
            :toggle-emoji-picker="toggleEmojiPicker"
            :message="message"
            :portal-slug="connectedPortalSlug"
            :new-conversation-modal-active="newConversationModalActive"
            @select-whatsapp-template="openWhatsappTemplateModal"
            @select-content-template="openContentTemplateModal"
            @select-whatsapp-flow="openWhatsappFlowModal"
            @toggle-insert-article="toggleInsertArticle"
            @toggle-quoted-reply="toggleQuotedReply"
          >
            <template #emoji-picker>
              <EmojiIconPicker
                v-on-clickaway="hideEmojiPicker"
                mode="emoji"
                class="emoji-dialog"
                @keydown.esc="hideEmojiPicker"
                @select="addIntoEditor($event.value)"
              />
            </template>
          </ReplyBottomPanel>
        </Transition>
      </div>

      <WhatsappTemplates
        :inbox-id="inbox.id"
        :show="showWhatsAppTemplatesModal"
        :contact="currentContact"
        :current-user="currentUser"
        :inbox-name="inbox.name || ''"
        :account-name="currentAccountName"
        :initial-template="shortcutWhatsAppTemplate"
        @close="hideWhatsappTemplatesModal"
        @on-send="onSendWhatsAppReply"
        @cancel="hideWhatsappTemplatesModal"
      />

      <WhatsappInteractiveModal
        ref="whatsappInteractiveModal"
        :inbox-id="inboxId"
        :conversation-id="currentChat?.id"
        :is-loading="isSendingInteractive"
        @send="onSendInteractiveMessage"
        @close="closeInteractiveMessageModal"
      />

      <PixPaymentModal
        v-if="isAWhatsAppCloudChannel"
        ref="pixPaymentModal"
        :key="currentChat?.id"
        :conversation-id="currentChat?.id"
        :is-loading="isSendingPix"
        @send="onSendPixPayment"
      />

      <WhatsappFlowSendModal
        v-if="showWhatsappFlowAction"
        ref="whatsappFlowModal"
        :inbox-id="inboxId"
        :account-id="currentChat.account_id"
        :can-reply="!!currentChat.can_reply"
        :is-loading="isSendingWhatsappFlow"
        @send="onSendWhatsappFlow"
      />

      <ContentTemplates
        :inbox-id="inbox.id"
        :show="showContentTemplatesModal"
        @close="hideContentTemplatesModal"
        @on-send="onSendContentTemplateReply"
        @cancel="hideContentTemplatesModal"
      />
    </div>

    <ConversationResolveAttributesModal
      ref="resolveAttributesModal"
      @submit="macroExecution.submitPendingAttributes"
      @close="macroExecution.dismissPendingAttributes"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative m-0 overflow-visible border-0 bg-n-surface-1 shadow-none transition-colors duration-200;

  &.is-focused {
    @apply bg-n-surface-1;
  }

  &.is-private {
    @apply bg-n-solid-amber;

    :deep(.empty-node::before) {
      @apply text-n-slate-11;
    }

    :deep(.empty-node:hover::before) {
      @apply text-n-slate-12;
    }

    :deep(.ProseMirror-menubar) {
      @apply text-n-slate-12;
    }
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative px-4 py-1.5;
}

.reply-box__composer-shell.is-compact {
  @apply relative mx-3 mb-3 grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-end rounded-2xl border border-n-weak bg-n-solid-2 p-2;

  &:focus-within {
    @apply border-n-brand/50;
  }

  .is-private & {
    @apply border-n-amber-7/50 bg-n-solid-1;
  }

  .reply-box__top {
    @apply min-w-0 px-2 py-2;
  }

  &:has(.composer-emoji) .reply-box__top {
    @apply pl-10;
  }

  :deep(.ProseMirror) {
    height: var(--editor-compact-height, auto);
    min-height: 1.5rem !important;
    max-height: 10rem;
  }

  :deep(.ProseMirror p) {
    @apply my-0;
    margin-bottom: 0 !important;
  }
}

.reply-box__note-settings {
  @apply mx-3 mb-2 rounded-xl border border-n-amber-7/50 bg-n-solid-amber px-3 py-2;

  &.is-joined {
    @apply mb-0 rounded-b-none border-b-0;
  }
}

.reply-box__note-expiry {
  @apply mb-0 flex min-w-0 items-center gap-2;
}

.reply-box__note-expiry-help {
  @apply mb-0 mt-1.5 text-xs leading-4 text-n-slate-11;
}

.reply-box__composer-shell.is-compact.is-note {
  @apply rounded-t-none border-n-amber-7/50;

  &:focus-within {
    @apply border-n-amber-9 ring-1 ring-inset ring-n-amber-9/30;
  }

  .reply-box__top {
    @apply text-n-amber-12;
  }
}

@media (max-width: 479px) {
  .reply-box__note-settings {
    @apply mx-2;
  }

  .reply-box__composer-shell.is-compact {
    @apply mx-2 grid-cols-1;

    &:has(.composer-emoji) .reply-box__top {
      @apply pl-2;
    }
  }
}

.reply-box__composer-shell.is-restricted {
  @apply mx-3 mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-3;

  .reply-box__top {
    @apply flex-1 p-0;
    min-width: min(20rem, 100%);
  }

  :deep(.reply-bottom-panel.is-restricted) {
    @apply m-0 w-auto shrink-0 p-0;
  }

  :deep(.restricted-template-button) {
    @apply h-9 text-sm shadow-none;
  }
}

.reply-box__restricted {
  @apply flex min-w-0 items-start gap-3;
}

.reply-box__restricted-icon {
  @apply grid size-8 shrink-0 place-items-center rounded-lg bg-n-ruby-3 text-n-ruby-11;
}

.emoji-dialog {
  position: relative;
  width: 100%;
  max-height: inherit;

  &::before {
    @apply hidden;
  }
}

.reply-box__mobile-collapsed-bar {
  display: none;
}

.reply-box.is-mobile-collapsed {
  @apply rounded-none border-t border-n-weak shadow-none;
}

.reply-box.is-mobile-collapsed .reply-box__content,
.reply-box.is-mobile-collapsed :deep(.reply-box-banner) {
  @apply hidden;
}

.reply-box.is-mobile-collapsed .reply-box__mobile-collapsed-bar {
  @apply flex w-full min-w-0 items-center gap-3 px-4 py-2.5 transition-colors duration-150 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand;
}

.reply-box__collapsed-icon {
  @apply grid size-8 shrink-0 place-items-center rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11;

  .is-private & {
    @apply border-n-amber-6/60 bg-n-solid-1 text-n-amber-11;
  }
}

.reply-box__collapsed-chip {
  @apply inline-flex h-6 shrink-0 items-center gap-1 rounded-md bg-n-alpha-2 px-1.5 text-xs font-medium text-n-slate-11;
}

.reply-box__collapsed-open {
  @apply inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-xs font-medium text-n-slate-12 transition-colors duration-150 group-hover:border-n-brand group-hover:text-n-brand;
}

@media (max-width: 767px) {
  .reply-box {
    @apply m-0 rounded-none;
  }

  .reply-box__top {
    @apply px-3 py-1;
  }

  :deep(.ProseMirror) {
    min-height: 1.5rem !important;
    font-size: 16px;
    max-height: 5rem !important;
  }
}
</style>
